import type { z } from 'zod';

import type { QueryCriteria, ResponseType } from './types';

const getValueAtPath = (obj: unknown, path: PropertyKey[]): unknown => {
  let value: unknown = obj;
  for (const key of path) {
    if (value && typeof value === 'object' && key in value) {
      value = (value as Record<string | number | symbol, unknown>)[key];
    } else {
      return undefined;
    }
  }
  return value;
};

export const logZodValidationError = (error: z.ZodError, response: unknown) => {
  // Simple, clear error logging
  console.error('═══════════════════════════════════════════════════');
  console.error('🔴 ZOD VALIDATION ERROR');
  console.error('═══════════════════════════════════════════════════');

  // Log each issue with actual values
  console.error(`Total Issues: ${error.issues.length.toString()}`);
  console.error('───────────────────────────────────────────────────');

  error.issues.forEach((issue, index) => {
    const path = issue.path.map(String).join('.') || '(root)';
    const actualValue = getValueAtPath(response, issue.path);

    console.error(`[${(index + 1).toString()}] Path: ${path}`);
    console.error(`    Code: ${issue.code}`);
    console.error(`    Message: ${issue.message}`);
    if ('expected' in issue) console.error(`    Expected: ${issue.expected}`);
    if ('received' in issue) console.error(`    Received: ${String(issue.received)}`);
    console.error(`    Actual Value: ${JSON.stringify(actualValue)}`);
    console.error(`    Value Type: ${actualValue === null ? 'null' : typeof actualValue}`);
    console.error('');
  });

  // Log response sample (truncated)
  console.error('───────────────────────────────────────────────────');
  console.error('Response Sample (first 1500 chars):');
  const responseStr = JSON.stringify(response, null, 2);
  console.error(responseStr.length > 1500 ? responseStr.substring(0, 1500) + '...' : responseStr);
  console.error('═══════════════════════════════════════════════════');
};

export const validateResponse = <T extends z.ZodType>(schema: T, response: unknown) => {
  const validationResult = schema.safeParse(response);

  if (!validationResult.success) {
    console.error('🔴 Validation failed! Logging details...', validationResult);
    logZodValidationError(validationResult.error, response);
    throw new Error(`Invalid API response: ${validationResult.error.message}`);
  }

  return validationResult.data as z.infer<T>;
};

export const toSearchParams = (criteria?: Record<string, string | boolean | number | undefined>): string => {
  if (!criteria) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(criteria)) {
    if (value !== undefined) {
      params.append(key, String(value));
    }
  }
  return params.toString();
};

/** Criteria key controlling the server-side row cap. */
export const MAX_ROWS_KEY = 'MaxRows';
/** `MaxRows` sentinel that removes the server row cap entirely — the param is omitted so the server returns all rows. */
export const MAX_ROWS_ALL = 'all';

export const buildQueryParams = (criteria?: QueryCriteria, resType: ResponseType = 'flat') => {
  const params = new URLSearchParams();

  if (criteria) {
    Object.entries(criteria).forEach(([key, value]) => {
      if (key === MAX_ROWS_KEY && value === MAX_ROWS_ALL) {
        return;
      }
      if (Array.isArray(value)) {
        for (const item of value) {
          params.append(`criteria.${key}`, item);
        }
      } else if (value !== undefined) {
        let strValue = '';
        if (typeof value === 'string') {
          strValue = value;
        }
        if (typeof value === 'number' || typeof value === 'boolean') {
          strValue = String(value);
        }
        params.append(`criteria.${key}`, strValue);
      }
    });
  }

  params.append('res.Type', resType);
  return params.toString();
};
